import { safeInternalRedirect } from "@/utils/internalRedirect";
// Import Auth Dependencies
import router from '@/app/router/router'
import axios from 'axios'
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";
import errorHandler from '@/utils/plugins/ErrorHandler'
import CONSTS from '@const/constants'

// Services
import notification from '@/utils/plugins/NotificationService'

// URL consts
const BASE_URL = CONSTS.BASE_URL
const LOGIN_URL = BASE_URL + '/login'
const REFRESH_TOKEN_URL = BASE_URL + '/api/token/refresh'
const LOGOUT_URL = BASE_URL + '/api/logout'
const CHANGE_PASSWORD_URL = BASE_URL + '/api/password/change';
const RECOVER_PASSWORD_REQUEST_URL = BASE_URL + '/api/password_recovery/request';
const RECOVER_PASSWORD_CONFIRM_URL = BASE_URL + '/api/password_recovery/confirm';

// El refresh token vive en una cookie HttpOnly (nunca accesible desde JS) — el
// navegador la envía sola en cada petición a REFRESH_TOKEN_URL/LOGOUT_URL siempre
// que withCredentials sea true. Este módulo solo gestiona el access token (JWT).

// Single-flight: si varias peticiones reciben 401 a la vez, solo se dispara una
// llamada real a /api/token/refresh; el resto espera esa misma promesa.
let refreshPromise = null;

export default {
    // User auth object
    user: {
        authenticated: false
    },
    // Intenta renovar el access token usando la cookie refresh_token (HttpOnly).
    // Usado por el interceptor de Api.js ante una respuesta 401.
    silentRefresh() {
        if (!refreshPromise) {
            refreshPromise = axios.post(REFRESH_TOKEN_URL, {}, { withCredentials: true })
                .then((response) => {
                    localStorage.setItem('token', response.data.token);
                    return response.data.token;
                })
                .finally(() => {
                    refreshPromise = null;
                });
        }
        return refreshPromise;
    },
    login(credentials) {
        const authStore = useAuthStore();
        const destination = safeInternalRedirect(router.currentRoute.value.query.redirect);
        const uiStore = useUiStore();
        uiStore.setLoading(true);
        return axios.post(LOGIN_URL, credentials, { withCredentials: true })
            .then(async res => {
                notification.addNotification('Sesión iniciada correctamente.', 'success', 6000);
                localStorage.setItem('token', res.data.token);
                this.user = res.data.user
                this.user.authenticated = true;
                // this.user.roles = ['ROLE_COLLABORATOR']
                await authStore.setUserLogged(this.user);
                await authStore.setAuth(true);
                await authStore.setShowLoginPopup(true);
                if (destination) {
                    await router.replace(destination);
                } else if(this.user.roles && this.user.roles.includes("ROLE_EXTERNAL_COLLABORATOR")){
                    await router.push({name: 'tracker'})
                }else{
                    await router.push({name: 'home'})
                }
                
                uiStore.setLoading(false);

            })
            .catch((err) => {
                this.user.authenticated = false;
                errorHandler(err, null, 'auth');
                uiStore.setLoading(false);
            });
    },
    // Recover password: solicita el email con el enlace de restablecimiento
    recover(email) {
        return axios.post(RECOVER_PASSWORD_REQUEST_URL, { email });
    },

    // Recover password: confirma el token recibido por email con la nueva contraseña
    confirmRecovery(token, password, repeatPassword) {
        return axios.post(RECOVER_PASSWORD_CONFIRM_URL, { token, password, repeatPassword });
    },

    // Logout: revoca el refresh token en el backend (cookie HttpOnly) y limpia el
    // estado local. Si la llamada al backend falla (red caída, cookie ya inválida...)
    // se limpia igualmente el estado local — el usuario siempre puede "salir".
    async logout({ preserveRedirect = false } = {}) {
        const current = router.currentRoute.value;
        const destination = preserveRedirect ? safeInternalRedirect(current.name === "login" ? current.query.redirect : current.fullPath) : null;
        const authStore = useAuthStore();
        const jwt = localStorage.getItem('token');

        // Sin token local no hubo sesión que revocar en este navegador (p.ej. se
        // visita la web sin estar logueado, o es un segundo logout tras uno ya
        // completado) — evita un POST /api/logout innecesario que siempre
        // respondería "No refresh_token found".
        if (jwt) {
            try {
                await axios.post(LOGOUT_URL, {}, {
                    withCredentials: true,
                    headers: { Authorization: `Bearer ${jwt}` },
                });
            } catch {
                // Ignorado a propósito: el logout local debe completarse igualmente.
            }
        }

        localStorage.removeItem('token')
        localStorage.removeItem('user')
        localStorage.removeItem('store')
        this.user.authenticated = false
        await authStore.setUserLogged(null)
        await authStore.setAuth(false)
        await router.push({name: 'login', query: destination ? { redirect: destination } : {}})
    },

    // Check if user is logged (token in LocalStorage)
    checkAuth() {
        let jwt = localStorage.getItem('token')
        this.user.authenticated = !!jwt
        return this.user.authenticated
    },

    // Change password (autenticado, exige la contraseña actual)
    changePassword(user) {
        let jwt = localStorage.getItem('token')
        return axios.post(CHANGE_PASSWORD_URL, user, {headers: {Authorization: `Bearer ${jwt}`}})
    }
}
